'use client';

/**
 * Галерея под ответом FAQ: фото сеткой миниатюр, видео плеером, остальное —
 * ссылками на скачивание. Внутри каждой группы порядок из админки
 * (sort_order); группы идут в таком порядке, потому что видео во всю ширину
 * не встаёт в сетку миниатюр.
 *
 * Полноразмерное фото открывается в нативном <dialog>: модальность, Escape,
 * недоступность страницы под оверлеем и возврат фокуса браузер даёт сам, без
 * библиотеки. Картинка в лайтбоксе — обычный <img> с оригиналом файла: здесь
 * нужен именно полный размер, а object-contain у next/image растянул бы
 * маленький скриншот на весь экран.
 */

import { useRef, useState, type MouseEvent } from 'react';
import { X } from 'lucide-react';
import { ProductImage } from '@/components/product/ProductImage';
import {
  attachmentKind,
  faqAttachmentPath,
  faqAttachmentUrl,
  type FaqAttachment,
} from '@/lib/faq/attachments';
import { FaqFileLink, FaqVideoPlayer } from './FaqAttachmentMedia';

function imageLabel(image: FaqAttachment): string {
  return image.alt?.trim() || image.file_name;
}

function ImageGrid({ images }: { images: FaqAttachment[] }) {
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [active, setActive] = useState<FaqAttachment | null>(null);

  const open = (image: FaqAttachment, event: MouseEvent<HTMLButtonElement>) => {
    triggerRef.current = event.currentTarget;
    setActive(image);
    dialogRef.current?.showModal();
  };

  // Сюда приходят все пути закрытия: кнопка, клик по оверлею, Escape
  const handleClose = () => {
    setActive(null);
    triggerRef.current?.focus();
  };

  return (
    <>
      <ul className="grid grid-cols-3 gap-2 sm:grid-cols-4">
        {images.map((image) => (
          <li key={image.id}>
            <button
              type="button"
              aria-haspopup="dialog"
              aria-label={`Open image: ${imageLabel(image)}`}
              onClick={(event) => open(image, event)}
              className="relative block aspect-square w-full overflow-hidden rounded-md border border-border-base bg-surface transition-colors hover:border-primary cursor-pointer"
            >
              <ProductImage
                src={faqAttachmentPath(image)}
                alt={image.alt ?? ''}
                fill
                sizes="(min-width: 640px) 192px, 33vw"
                className="object-cover"
              />
            </button>
          </li>
        ))}
      </ul>

      {/* Клик в любом месте лайтбокса закрывает его — и по фото, и по оверлею */}
      <dialog
        ref={dialogRef}
        onClose={handleClose}
        onClick={() => dialogRef.current?.close()}
        aria-label={active ? imageLabel(active) : undefined}
        className="faq-lightbox m-0 h-full max-h-none w-full max-w-none items-center justify-center bg-[var(--color-overlay)] p-4 backdrop-blur-[var(--overlay-blur)] backdrop:bg-transparent open:flex cursor-pointer"
      >
        {active && (
          <>
            <button
              type="button"
              autoFocus
              aria-label="Close"
              className="absolute right-4 top-4 rounded-full bg-surface p-2 text-text-primary shadow-lg cursor-pointer"
            >
              <X size={20} aria-hidden="true" />
            </button>
            {/* eslint-disable-next-line @next/next/no-img-element -- нужен оригинал в натуральную величину, см. шапку файла */}
            <img
              src={faqAttachmentUrl(active)}
              alt={active.alt ?? ''}
              className="max-h-full max-w-full rounded-lg object-contain shadow-xl"
            />
          </>
        )}
      </dialog>
    </>
  );
}

export function FaqGallery({ attachments }: { attachments: FaqAttachment[] }) {
  if (attachments.length === 0) return null;

  const images = attachments.filter((file) => attachmentKind(file.mime_type) === 'image');
  const videos = attachments.filter((file) => attachmentKind(file.mime_type) === 'video');
  const files = attachments.filter((file) => attachmentKind(file.mime_type) === 'file');

  return (
    <div className="mt-4 space-y-4">
      {images.length > 0 && <ImageGrid images={images} />}

      {videos.map((video) => (
        <figure key={video.id}>
          <FaqVideoPlayer file={video} />
          {video.alt?.trim() && (
            <figcaption className="mt-2 text-sm text-text-secondary">{video.alt}</figcaption>
          )}
        </figure>
      ))}

      {files.length > 0 && (
        <ul className="space-y-2">
          {files.map((file) => (
            <li key={file.id}>
              <FaqFileLink file={file} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
